import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "tailwindcss/tailwind.css";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useEffect, useState, useCallback } from "react";
import { API_BASE_URLS } from "../constants/api";
import { auth, apiUtils } from "../utils";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const heartRateOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    title: {
      display: false,
    },
    tooltip: { enabled: false },
    datalabels: {
      display: true,
      align: "top" as const,
      anchor: "end" as const,
      color: "#232B3A",
      font: { weight: "normal" as const, size: 12 },
      formatter: function (value: number) {
        return value;
      },
    },
  },
  scales: {
    x: {
      grid: {
        display: true,
        color: "#e5e7eb",
        borderDash: [10, 10],
        drawBorder: false,
      },
      display: true,
      ticks: {
        color: "#6B7280",
        font: { size: 10 },
      },
    },
    y: {
      grid: {
        color: "#e5e7eb",
        borderDash: [10, 10],
        drawBorder: false,
      },
      min: 0,
      max: 200,
      ticks: {
        stepSize: 50,
        display: false,
      },
      display: true,
    },
  },
};

const bloodPressureOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    title: {
      display: false,
    },
    tooltip: { enabled: false },
    datalabels: {
      display: true,
      align: "top" as const,
      anchor: "end" as const,
      color: "#232B3A",
      font: { weight: "normal" as const, size: 12 },
      formatter: function (value: any) {
        if (Array.isArray(value)) {
          return `${value[1]}/${value[0]}`;
        }
        return value;
      },
    },
  },
  scales: {
    x: {
      grid: {
        display: true,
        color: "#e5e7eb",
        borderDash: [10, 10],
        drawBorder: false,
      },
      display: true,
      ticks: {
        color: "#6B7280",
        font: { size: 10 },
      },
    },
    y: {
      grid: {
        color: "#e5e7eb",
        borderDash: [10, 10],
        drawBorder: false,
      },
      min: 0,
      max: 200,
      ticks: {
        stepSize: 50,
        display: false,
      },
      display: true,
    },
  },
  elements: {
    bar: {
      borderWidth: 1,
      borderRadius: 4,
      borderSkipped: false,
    },
  },
};

const bloodOxygenOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    title: {
      display: false,
    },
    tooltip: { enabled: false },
    datalabels: {
      display: true,
      align: "top" as const,
      anchor: "end" as const,
      color: "#232B3A",
      font: { weight: "normal" as const, size: 12 },
      formatter: function (value: number) {
        return `${value}%`;
      },
    },
  },
  scales: {
    x: {
      grid: {
        display: true,
        color: "#e5e7eb",
        borderDash: [10, 10],
        drawBorder: false,
      },
      display: true,
      ticks: {
        color: "#6B7280",
        font: { size: 10 },
      },
    },
    y: {
      grid: {
        color: "#e5e7eb",
        borderDash: [10, 10],
        drawBorder: false,
      },
      min: 70,
      max: 105,
      ticks: {
        stepSize: 10,
        callback: (value: number | string) => `${value}%`,
        display: false,
      },
      display: true,
    },
  },
  elements: {
    bar: {
      borderWidth: 1,
      borderRadius: 4,
      borderSkipped: false,
    },
  },
};

interface HealthMetricsDashboardProps {
  vitals: any;
  patientId: string;
  heartRateDataForChart: number[];
  setHeartRateDataForChart: (data: number[]) => void;
  bloodPressureDataForChart: [number, number][];
  setBloodPressureDataForChart: (data: [number, number][]) => void;
  bloodOxygenDataForChart: number[];
  setBloodOxygenDataForChart: (data: number[]) => void;
  heartRateTimeLabels: string[];
  setHeartRateTimeLabels: (labels: string[]) => void;
  bloodPressureTimeLabels: string[];
  setBloodPressureTimeLabels: (labels: string[]) => void;
  bloodOxygenTimeLabels: string[];
  setBloodOxygenTimeLabels: (labels: string[]) => void;
  latestHeartRate: number | null;
  setLatestHeartRate: (value: number | null) => void;
  latestBloodPressure: { systolic: number; diastolic: number } | null;
  setLatestBloodPressure: (
    value: { systolic: number; diastolic: number } | null
  ) => void;
  latestBloodOxygen: number | null;
  setLatestBloodOxygen: (value: number | null) => void;
  latestHeartRateTime: string;
  setLatestHeartRateTime: (value: string) => void;
  latestBloodPressureTime: string;
  setLatestBloodPressureTime: (value: string) => void;
  latestBloodOxygenTime: string;
  setLatestBloodOxygenTime: (value: string) => void;
}

export default function HealthMetricsDashboard({
  patientId,
  heartRateDataForChart,
  setHeartRateDataForChart,
  bloodPressureDataForChart,
  setBloodPressureDataForChart,
  bloodOxygenDataForChart,
  setBloodOxygenDataForChart,
  heartRateTimeLabels,
  setHeartRateTimeLabels,
  bloodPressureTimeLabels,
  setBloodPressureTimeLabels,
  bloodOxygenTimeLabels,
  setBloodOxygenTimeLabels,
  latestHeartRate,
  setLatestHeartRate,
  latestBloodPressure,
  setLatestBloodPressure,
  latestBloodOxygen,
  setLatestBloodOxygen,
  latestHeartRateTime,
  setLatestHeartRateTime,
  latestBloodPressureTime,
  setLatestBloodPressureTime,
  latestBloodOxygenTime,
  setLatestBloodOxygenTime,
}: HealthMetricsDashboardProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasInitialData, setHasInitialData] = useState(false);

  // Helper function to format timestamp
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    return `${displayHours}:${displayMinutes} ${ampm}`;
  };

  // Helper function to format timestamp for chart labels (first label has AM/PM, others don't)
  const formatTimestampForChart = (
    timestamp: string,
    isFirst: boolean = false
  ) => {
    const date = new Date(timestamp);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const displayHours = hours % 12 || 12;
    const displayMinutes = `:${minutes.toString().padStart(2, "0")}`;

    if (isFirst) {
      const ampm = hours >= 12 ? "pm" : "am";
      return `${displayHours}${displayMinutes} ${ampm}`;
    } else {
      return `${displayHours}${displayMinutes}`;
    }
  };

  // Helper function to generate padding time labels when fewer than 6 records exist
  const generatePaddingTimeLabels = (
    existingTimestamps: string[],
    targetCount: number = 6
  ): string[] => {
    if (existingTimestamps.length === 0) {
      // If no existing timestamps, generate labels based on current time (newest to oldest)
      const now = new Date();
      const labels = [];
      // Generate labels from newest (current time) to oldest (going back in time)
      for (let i = 0; i < targetCount; i++) {
        const time = new Date(now.getTime() - i * 15 * 60 * 1000);
        const hours = time.getHours();
        const minutes = time.getMinutes();
        const displayHours = hours % 12 || 12;
        const displayMinutes = `:${minutes.toString().padStart(2, "0")}`;
        if (i === 0) {
          // First label (newest) should have AM/PM
          const ampm = hours >= 12 ? "pm" : "am";
          labels.push(`${displayHours}${displayMinutes} ${ampm}`);
        } else {
          labels.push(`${displayHours}${displayMinutes}`);
        }
      }
      return labels;
    }

    // If we have existing timestamps, generate labels based on the pattern
    const labels: string[] = [];
    const existingCount = existingTimestamps.length;
    const paddingCount = targetCount - existingCount;

    // Sort existing timestamps in descending order (newest first)
    const sortedTimestamps = [...existingTimestamps].sort(
      (a, b) => new Date(b).getTime() - new Date(a).getTime()
    );

    // Add existing timestamps FIRST in reverse chronological order (newest first)
    // The first existing timestamp (newest) should have AM/PM if it's the first label overall
    sortedTimestamps.forEach((timestamp, index) => {
      const isFirstLabel = index === 0;
      labels.push(formatTimestampForChart(timestamp, isFirstLabel));
    });

    // Calculate average interval between existing timestamps for padding
    let avgInterval = 15 * 60 * 1000; // Default to 15 minutes
    if (sortedTimestamps.length > 1) {
      const intervals: number[] = [];
      // Calculate intervals going backwards (from newest to oldest)
      for (let i = 1; i < sortedTimestamps.length; i++) {
        intervals.push(
          new Date(sortedTimestamps[i - 1]).getTime() -
            new Date(sortedTimestamps[i]).getTime()
        );
      }
      avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
    } else if (sortedTimestamps.length === 1) {
      // If only one timestamp, use 15-minute intervals for padding
      avgInterval = 15 * 60 * 1000;
    }

    // Generate padding labels AFTER the existing timestamps (going backwards in time from oldest)
    const oldestTimestamp = new Date(
      sortedTimestamps[sortedTimestamps.length - 1]
    );
    for (let i = 1; i <= paddingCount; i++) {
      const time = new Date(oldestTimestamp.getTime() - i * avgInterval);
      const hours = time.getHours();
      const minutes = time.getMinutes();
      const displayHours = hours % 12 || 12;
      const displayMinutes = `:${minutes.toString().padStart(2, "0")}`;
      labels.push(`${displayHours}${displayMinutes}`);
    }

    return labels;
  };

  // Fetch patient data
  const fetchPatientData = useCallback(async () => {
    try {
      // Only show loading on initial load, not on subsequent requests
      if (!hasInitialData) {
        setIsLoading(true);
      }
      const requestOptions = apiUtils.createRequestOptions("GET");
      const response = await fetch(
        `${API_BASE_URLS.HELIUS_SERVICE}/patients/${patientId}`,
        requestOptions
      );
      if (response.ok) {
        const data = await response.json();
        const vitals = data.vitals;

        // Helper functions to process vitals data
        const getLatestVitalFromData = (bodyType: string) => {
          if (!vitals) return undefined;
          let all: any[] = [];
          Object.values(vitals).forEach((arr: any) => {
            if (Array.isArray(arr)) {
              all = all.concat(arr.filter((v: any) => v.bodyType === bodyType));
            }
          });
          if (all.length === 0) return undefined;
          all.sort(
            (a: any, b: any) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime()
          );
          return all[0];
        };

        const getLatestVitalsForChartFromData = (bodyType: string) => {
          if (!vitals) return [];
          let all: any[] = [];
          Object.values(vitals).forEach((arr: any) => {
            if (Array.isArray(arr)) {
              all = all.concat(arr.filter((v: any) => v.bodyType === bodyType));
            }
          });
          if (all.length === 0) return [];
          all.sort(
            (a: any, b: any) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime()
          );
          return all.slice(0, 6); // Get latest 5 records
        };

        // Update latest values
        const bpVital = getLatestVitalFromData("BLOOD_PRESSURE");
        const hrVital = getLatestVitalFromData("HEART_RATE");
        const boVital = getLatestVitalFromData("BLOOD_OXYGEN");

        if (
          bpVital &&
          bpVital.body &&
          bpVital.body.systolic !== undefined &&
          bpVital.body.diastolic !== undefined
        ) {
          setLatestBloodPressure({
            systolic: bpVital.body.systolic,
            diastolic: bpVital.body.diastolic,
          });
        } else {
          setLatestBloodPressure(null);
        }

        if (hrVital && hrVital.body && hrVital.body.value !== undefined) {
          setLatestHeartRate(hrVital.body.value);
        } else {
          setLatestHeartRate(null);
        }

        if (boVital && boVital.body && boVital.body.value !== undefined) {
          setLatestBloodOxygen(boVital.body.value);
        } else {
          setLatestBloodOxygen(null);
        }

        // Update chart data
        const heartRateVitals = getLatestVitalsForChartFromData("HEART_RATE");
        const bloodPressureVitals =
          getLatestVitalsForChartFromData("BLOOD_PRESSURE");
        const bloodOxygenVitals =
          getLatestVitalsForChartFromData("BLOOD_OXYGEN");

        // Extract heart rate data and timestamps
        const heartRateData = heartRateVitals.map((vital) => vital.body.value);
        const heartRateTimestamps = heartRateVitals.map(
          (vital) => vital.created_at
        );
        // Pad data to 6 items - actual entries first, then nulls
        const paddedHeartRateData = [...heartRateData];
        while (paddedHeartRateData.length < 6) {
          paddedHeartRateData.push(null as any);
        }
        const heartRateLabels = generatePaddingTimeLabels(
          heartRateTimestamps,
          6
        );
        setHeartRateDataForChart(paddedHeartRateData);
        setHeartRateTimeLabels(heartRateLabels);

        // Extract blood pressure data and timestamps
        const bloodPressureData = bloodPressureVitals.map(
          (vital) =>
            [vital.body.diastolic, vital.body.systolic] as [number, number]
        );
        const bloodPressureTimestamps = bloodPressureVitals.map(
          (vital) => vital.created_at
        );
        // Pad data to 6 items - actual entries first, then nulls
        const paddedBloodPressureData = [...bloodPressureData];
        while (paddedBloodPressureData.length < 6) {
          paddedBloodPressureData.push([null, null] as any);
        }
        const bloodPressureLabels = generatePaddingTimeLabels(
          bloodPressureTimestamps,
          6
        );
        setBloodPressureDataForChart(paddedBloodPressureData);
        setBloodPressureTimeLabels(bloodPressureLabels);

        // Extract blood oxygen data and timestamps
        const bloodOxygenData = bloodOxygenVitals.map(
          (vital) => vital.body.value
        );
        const bloodOxygenTimestamps = bloodOxygenVitals.map(
          (vital) => vital.created_at
        );
        // Pad data to 6 items - actual entries first, then nulls
        const paddedBloodOxygenData = [...bloodOxygenData];
        while (paddedBloodOxygenData.length < 6) {
          paddedBloodOxygenData.push(null as any);
        }
        const bloodOxygenLabels = generatePaddingTimeLabels(
          bloodOxygenTimestamps,
          6
        );
        setBloodOxygenDataForChart(paddedBloodOxygenData);
        setBloodOxygenTimeLabels(bloodOxygenLabels);

        // Set the latest reading times from actual timestamps
        if (hrVital && hrVital.created_at) {
          setLatestHeartRateTime(formatTimestamp(hrVital.created_at));
        }
        if (bpVital && bpVital.created_at) {
          setLatestBloodPressureTime(formatTimestamp(bpVital.created_at));
        }
        if (boVital && boVital.created_at) {
          setLatestBloodOxygenTime(formatTimestamp(boVital.created_at));
        }

        // Mark that we have initial data
        if (!hasInitialData) {
          setHasInitialData(true);
        }
      }
    } catch (error) {
      console.error("Error fetching patient data:", error);
    } finally {
      if (!hasInitialData) {
        setIsLoading(false);
      }
    }
  }, [patientId, hasInitialData]);

  // Set up interval to fetch data every 2 minutes
  useEffect(() => {
    fetchPatientData(); // Initial fetch

    const interval = setInterval(() => {
      fetchPatientData();
    }, 2 * 60 * 1000); // 2 minutes for testing

    return () => clearInterval(interval);
  }, [patientId, fetchPatientData]);

  const bloodPressure = latestBloodPressure
    ? `${latestBloodPressure.systolic}/${latestBloodPressure.diastolic}`
    : null;
  const heartRate = latestHeartRate;
  const bloodOxygen = latestBloodOxygen;

  // Check if data is available - both chart data and latest value must exist
  const hasHeartRateData =
    heartRateDataForChart.length > 0 && latestHeartRate !== null;
  const hasBloodPressureData =
    bloodPressureDataForChart.length > 0 && latestBloodPressure !== null;
  const hasBloodOxygenData =
    bloodOxygenDataForChart.length > 0 && latestBloodOxygen !== null;
  // Use actual timestamp labels from database (set in fetchPatientData)

  // Create chart data with latest values
  const heartRateData = {
    labels:
      heartRateTimeLabels.length > 0 ? heartRateTimeLabels : Array(6).fill(""),
    datasets: [
      {
        label: "Heart Rate",
        data: heartRateDataForChart,
        borderColor: "#F43F5E",
        backgroundColor: "rgba(244,63,94,0.1)",
        tension: 0.1,
        fill: false,
        pointRadius: 4,
        pointBackgroundColor: "#FFFFFF",
        pointBorderColor: "#F43F5E",
        pointBorderWidth: 2,
        datalabels: {
          align: "top" as const,
          anchor: "end" as const,
          color: "#232B3A",
          font: { weight: "normal" as const, size: 12 },
          formatter: function (value: number) {
            return value;
          },
          display: true,
        },
      },
    ],
  };

  const bloodPressureData = {
    labels:
      bloodPressureTimeLabels.length > 0
        ? bloodPressureTimeLabels
        : Array(6).fill(""),
    datasets: [
      {
        label: "Blood Pressure",
        data: bloodPressureDataForChart,
        backgroundColor: bloodPressureDataForChart.map(
          ([diastolic, systolic]) =>
            systolic >= 140 || diastolic >= 90
              ? "#FB7185"
              : systolic >= 120 || diastolic >= 80
              ? "#FB7185"
              : "#2DD4BF"
        ),
        borderColor: bloodPressureDataForChart.map(([diastolic, systolic]) =>
          systolic >= 140 || diastolic >= 90
            ? "#FB7185"
            : systolic >= 120 || diastolic >= 80
            ? "#FB7185"
            : "#2DD4BF"
        ),
        borderWidth: 1,
        barThickness: 50,
        maxBarThickness: 50,
      },
    ],
  };

  const bloodOxygenData = {
    labels:
      bloodOxygenTimeLabels.length > 0
        ? bloodOxygenTimeLabels
        : Array(6).fill(""),
    datasets: [
      {
        label: "Blood Oxygen",
        data: bloodOxygenDataForChart, // Use latest blood oxygen as last value
        backgroundColor: bloodOxygenDataForChart.map((value) =>
          value < 90 ? "#FB7185" : value < 95 ? "#FB7185" : "#2DD4BF"
        ),
        borderColor: bloodOxygenDataForChart.map((value) =>
          value < 90 ? "#FB7185" : value < 95 ? "#FB7185" : "#2DD4BF"
        ),
        borderWidth: 1,
        barThickness: 50,
        maxBarThickness: 50,
      },
    ],
  };

  // Threshold functions from DataTable
  const getHeartRateColor = (heartRate: number) => {
    if (heartRate < 60 || heartRate > 100) {
      return "text-rag-red"; // High risk
    } else if (heartRate < 70 || heartRate > 90) {
      return "text-rag-amber"; // Medium risk
    } else {
      return "text-rag-green"; // Low risk
    }
  };

  const getBloodPressureColor = (systolic: number, diastolic: number) => {
    if (systolic >= 140 || diastolic >= 90) {
      return "text-rag-red"; // High risk
    } else if (systolic >= 120 || diastolic >= 80) {
      return "text-rag-amber"; // Medium risk
    } else {
      return "text-rag-green"; // Low risk
    }
  };

  const getBloodOxygenColor = (bloodOxygen: number) => {
    if (bloodOxygen < 90) {
      return "text-rag-red"; // High risk
    } else if (bloodOxygen < 95) {
      return "text-rag-amber"; // Medium risk
    } else {
      return "text-rag-green"; // Low risk
    }
  };

  // Show loading state while data is being fetched
  if (isLoading) {
    return (
      <div className="w-full flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading health metrics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4 mb-6 overflow-hidden">
      {/* Heart Rate Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-5 overflow-hidden">
        <div className="flex items-center justify-between w-full">
          {/* Left Panel - Icon and Current Value */}
          <div className="flex flex-col items-start mb-20">
            <div className="flex items-center gap-2 mb-3">
              <img
                src="/images/heart_rate.png"
                alt="Heart Rate"
                className="h-6 w-6"
              />
              <span className="text-lg font-bold text-gray-800">
                Heart Rate
              </span>
            </div>
            {hasHeartRateData && latestHeartRate !== null ? (
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span
                    className={`text-3xl font-bold ${getHeartRateColor(
                      latestHeartRate
                    )}`}
                  >
                    {latestHeartRate}
                  </span>
                  <span className="text-sm text-gray-500 font-semibold">
                    BPM
                  </span>
                  {(latestHeartRate < 60 || latestHeartRate > 100) && (
                    <svg
                      className="h-5 w-5 text-rag-red"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M7 11l5-5m0 0l5 5m-5-5v12"
                      />
                    </svg>
                  )}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {latestHeartRateTime}
                </div>
              </div>
            ) : (
              <></>
            )}
          </div>

          {/* Right Panel - Line Chart */}
          <div className="flex-1 h-48 ml-6">
            {hasHeartRateData ? (
              <Line data={heartRateData} options={heartRateOptions} />
            ) : (
              <div className="h-full flex items-center justify-center">
                <span className="text-gray-500 text-sm">No data available</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Blood Pressure Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-5 overflow-hidden">
        <div className="flex items-center justify-between w-full">
          {/* Left Panel - Icon and Current Value */}
          <div className="flex flex-col items-start mb-20">
            <div className="flex items-center gap-2 mb-3">
              <img
                src="/images/blood_pressure.png"
                alt="Blood Pressure"
                className="h-6 w-6"
              />
              <span className="text-lg font-bold text-gray-800">
                Blood Pressure
              </span>
            </div>
            {hasBloodPressureData &&
            latestBloodPressure !== null &&
            bloodPressure !== null ? (
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span
                    className={`text-3xl font-bold ${getBloodPressureColor(
                      latestBloodPressure.systolic,
                      latestBloodPressure.diastolic
                    )}`}
                  >
                    {bloodPressure}
                  </span>
                  <span className="text-sm text-gray-500 font-semibold">
                    mmHg
                  </span>
                  {(latestBloodPressure.systolic >= 140 ||
                    latestBloodPressure.diastolic >= 90) && (
                    <svg
                      className="h-5 w-5 text-rag-red"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M7 11l5-5m0 0l5 5m-5-5v12"
                      />
                    </svg>
                  )}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {latestBloodPressureTime}
                </div>
              </div>
            ) : (
              <></>
            )}
          </div>

          {/* Right Panel - Bar Chart */}
          <div className="flex-1 h-48 ml-6">
            {hasBloodPressureData ? (
              <Bar data={bloodPressureData} options={bloodPressureOptions} />
            ) : (
              <div className="h-full flex items-center justify-center">
                <span className="text-gray-500 text-sm">No data available</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Blood Oxygen Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-5 overflow-hidden">
        <div className="flex items-center justify-between w-full">
          {/* Left Panel - Icon and Current Value */}
          <div className="flex flex-col items-start mb-20">
            <div className="flex items-center gap-2 mb-3">
              <img
                src="/images/blood_oxygen.png"
                alt="Blood Oxygen"
                className="h-6 w-6"
              />
              <span className="text-lg font-bold text-gray-800">
                Blood Oxygen
              </span>
            </div>
            {hasBloodOxygenData && latestBloodOxygen !== null ? (
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span
                    className={`text-3xl font-bold ${getBloodOxygenColor(
                      latestBloodOxygen
                    )}`}
                  >
                    {latestBloodOxygen}%
                  </span>
                  {latestBloodOxygen < 90 && (
                    <svg
                      className="h-5 w-5 text-rag-red"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M7 11l5-5m0 0l5 5m-5-5v12"
                      />
                    </svg>
                  )}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {latestBloodOxygenTime}
                </div>
              </div>
            ) : (
              <></>
            )}
          </div>

          {/* Right Panel - Bar Chart */}
          <div className="flex-1 h-48 ml-6">
            {hasBloodOxygenData ? (
              <Bar data={bloodOxygenData} options={bloodOxygenOptions} />
            ) : (
              <div className="h-full flex items-center justify-center">
                <span className="text-gray-500 text-sm">No data available</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
